import moment from 'moment';
import styles from './SongCharts.module.css';
import { LineChart, LineChartDataSet as DataSet } from '@round/whitelabel-ui';
import { Tooltip } from '@round/whitelabel-ui';
import { numberWithCommas } from 'helpers';
import { useCreations } from './hooks/useCreations';
import { usePlatformFilters } from '../PlatformFilters/usePlatformFilters';
import { creatorbase } from '@round/api';
import pick from 'lodash/pick';
import { useMemo, useState } from 'react';
import { timeSeriesFieldLabels } from './helpers';
import { DateRangeFilter } from '@round/whitelabel-ui';
import cn from 'classnames';
import PlatformFilters from '../PlatformFilters/PlatformFilters';
import { useSpotifyPopularityChart } from './hooks/useSpotifyPopularityChart';

type Props = {
    songId: number | null | undefined;
    className?: string;
};

const SongCharts = ({ songId, className }: Props) => {
    const { activePlatforms } = usePlatformFilters();
    const today = moment();
    const twoWeeksAgo = moment().subtract(2, 'weeks');

    const [date, setDate] = useState<[Date] | [Date, Date] | null | undefined>([twoWeeksAgo.toDate(), today.toDate()]);
    const [startDate, endDate] = date ?? [];

    const {
        data: creationsData,
        isError: isCreationsError,
        isFetching: isCreationsFetching,
    } = useCreations({
        start_date: (startDate ? moment(startDate) : twoWeeksAgo).format('YYYY-MM-DD'),
        end_date: (endDate ? moment(endDate) : today).format('YYYY-MM-DD'),
        songId,
    });

    const dataSets = useMemo(() => {
        return creationsData
            ? Object.entries(creationsData).reduce(
                  (acc, [key, value]) => {
                      if (!!activePlatforms.find((platform) => key.includes(platform)) && !!value.length) {
                          acc[key as creatorbase.TimeSeriesField] = {
                              data: value.map((point) => ({
                                  x: point.timestamp,
                                  y: point.value,
                              })),
                              borderColor: key.includes('tiktok') ? '#00f2ea' : '#e1306c',
                              label: timeSeriesFieldLabels[key as creatorbase.TimeSeriesField],
                          };
                      }

                      return acc;
                  },
                  {} as Record<creatorbase.TimeSeriesField, DataSet>
              )
            : null;
    }, [creationsData, activePlatforms]);

    const {
        data: popularityData,
        isError: isPopularityError,
        isLoading: isPopularityLoading,
        shouldHideChart: shouldHidePopularityChart,
    } = useSpotifyPopularityChart({
        start_date: (startDate ? moment(startDate) : twoWeeksAgo).format('YYYY-MM-DD'),
        end_date: (endDate ? moment(endDate) : today).format('YYYY-MM-DD'),
        songId,
    });

    return (
        <div className={cn(styles.container, className)}>
            <div className={styles.filters}>
                <DateRangeFilter value={date} onChange={setDate} maxDate={today.toDate()} />
                <PlatformFilters />
            </div>

            <div className={styles.charts}>
                {!shouldHidePopularityChart ? (
                    <div className={cn(styles.chartTile, styles.popularityChart)}>
                        <Tooltip.Root>
                            {(anchor, setAnchor, underlineClassName) => (
                                <>
                                    <p className={cn(styles.title, underlineClassName)} ref={setAnchor}>
                                        Spotify Popularity
                                    </p>
                                    <Tooltip anchorElement={anchor}>
                                        <Tooltip.Body>
                                            The Spotify Popularity Index is a 0-100 score based on the total number of
                                            plays for a track and how recent those plays are
                                        </Tooltip.Body>
                                    </Tooltip>
                                </>
                            )}
                        </Tooltip.Root>
                        <div className={styles.chartContainer}>
                            <LineChart
                                isLoading={isPopularityLoading}
                                hasError={isPopularityError}
                                dataSets={popularityData ? [popularityData] : []}
                                renderTooltip={({ point, dataset }) => (
                                    <>
                                        <Tooltip.Title>{moment(point.x).format('D MMM YYYY')}</Tooltip.Title>
                                        <Tooltip.Body>
                                            <dl className={styles.chartTooltipStats}>
                                                <div className={styles.chartTooltipStatsItem}>
                                                    <dt>{dataset.label}</dt>
                                                    <dd>{numberWithCommas(point.y)}</dd>
                                                </div>
                                            </dl>
                                        </Tooltip.Body>
                                    </>
                                )}
                            />
                        </div>
                    </div>
                ) : null}

                <div className={styles.chartTile}>
                    <p className={styles.title}>Total creations</p>
                    <div className={styles.chartContainer}>
                        <LineChart
                            isLoading={isCreationsFetching}
                            hasError={isCreationsError}
                            dataSets={
                                dataSets
                                    ? Object.values(pick(dataSets, ['tiktok_daily_totals', 'instagram_daily_totals']))
                                    : []
                            }
                            renderTooltip={({ point, dataset }) => (
                                <>
                                    <Tooltip.Title>{moment(point.x).format('D MMM YYYY')}</Tooltip.Title>
                                    <Tooltip.Body>
                                        <dl className={styles.chartTooltipStats}>
                                            <div className={styles.chartTooltipStatsItem}>
                                                <dt>{dataset.label}</dt>
                                                <dd>{numberWithCommas(point.y)}</dd>
                                            </div>
                                        </dl>
                                    </Tooltip.Body>
                                </>
                            )}
                        />
                    </div>
                </div>

                <div className={styles.chartTile}>
                    <p className={styles.title}>Daily creations</p>
                    <div className={styles.chartContainer}>
                        <LineChart
                            isLoading={isCreationsFetching}
                            hasError={isCreationsError}
                            dataSets={
                                dataSets
                                    ? Object.values(pick(dataSets, ['tiktok_daily_change', 'instagram_daily_change']))
                                    : []
                            }
                            renderTooltip={({ point, dataset }) => (
                                <>
                                    <Tooltip.Title>{moment(point.x).format('D MMM YYYY')}</Tooltip.Title>
                                    <Tooltip.Body>
                                        <dl className={styles.chartTooltipStats}>
                                            <div className={styles.chartTooltipStatsItem}>
                                                <dt>{dataset.label}</dt>
                                                <dd>{numberWithCommas(point.y)}</dd>
                                            </div>
                                        </dl>
                                    </Tooltip.Body>
                                </>
                            )}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SongCharts;
